import { DateTime } from 'luxon';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { date, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Botao, CaixaDeTexto, Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import LayoutDefault from 'Component/Layout';
import FechamentoDeFolha from 'Interface/FechamentosDeFolha';
import {
  useEditarFechamentoDeFolhaMutation,
  useRecuperarFechamentoDeFolhaQuery,
} from 'View/Service/Default/FechamentosDeFolha';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_id: number().required('Obrigatório'),
  inicio: date().required('Obrigatório'),
  termino: date().required('Obrigatório'),
  salario: number().required('Obrigatório'),
  valor: number().nullable().optional(),
  ajuda_de_custo: number().nullable().optional(),
  premio_de_metas_vendedores: number().nullable().optional(),
  premio_de_metas_geral: number().nullable().optional(),
  vale_transporte: number().nullable().optional(),
  adiantamento: number().nullable().optional(),
  farmacia: number().nullable().optional(),
  medico: number().nullable().optional(),
  odonto: number().nullable().optional(),
  consignado: number().nullable().optional(),
  descontos: number().nullable().optional(),
  observacoes: string().nullable().optional(),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { usuarioId = 0, id = 0 } = useParams();
  const [editarFolha] = useEditarFechamentoDeFolhaMutation();

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const { data: { fechamento_de_folha } = {} } =
    useRecuperarFechamentoDeFolhaQuery(id);

  const enviar = async (dados: FechamentoDeFolha): Promise<void> => {
    try {
      await editarFolha(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editar arquivo de fechamento de folha"
        descricao="Preencha o formulário e clique em enviar para editar o arquivo"
      />
      <Form
        onSubmit={enviar}
        regras={regras}
        inicial={fechamento_de_folha}
        desabilitado={fechamento_de_folha?.assinado as unknown as boolean}
      >
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                    autoSelecao
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="inicio"
                    type="date"
                    rotulo="Data inicial"
                    prefixar={<Icone nome="calendar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="termino"
                    type="date"
                    rotulo="Data final"
                    prefixar={<Icone nome="calendar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="salario"
                    rotulo="Salário"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="valor"
                    rotulo="Comissão"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="ajuda_de_custo"
                    rotulo="Ajuda de custo/combustível"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="premio_de_metas_vendedores"
                    rotulo="Prêmio de metas - Vencedores"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="premio_de_metas_geral"
                    rotulo="Prêmio de metas - Geral"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="vale_transporte"
                    rotulo="Vale transporte"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="adiantamento"
                    rotulo="Adiantamento"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="farmacia"
                    rotulo="Con. farmácia"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="medico"
                    rotulo="Con. Médico"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="odonto"
                    rotulo="Con. Odonto"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="consignado"
                    rotulo="Emprestimo Consignado"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="descontos"
                    rotulo="Outros descontos"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto name="observacoes" rotulo="Observações" />
                </div>
                <div className="col-md-12">
                  {fechamento_de_folha?.assinatura && (
                    <Imagem
                      src={fechamento_de_folha.assinatura}
                      className="img-fluid"
                    />
                  )}
                  {fechamento_de_folha?.data_da_assinatura && (
                    <p>
                      Assinado em{' '}
                      {fechamento_de_folha.data_da_assinatura
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Editar;
